import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Flex, useMediaQuery } from '@chakra-ui/react';
import { changeLocale } from 'gatsby-plugin-intl';
import I18n from './I18n';
import MenuIcon from './svgIcon/menu';
import MenuCloseIcon from './svgIcon/menuClose';
import download from '../utils/download';
import CustomLink from './customLink';
import DropDownMenu from './DropDownMenu';
import Logo from './logo';
import ButtonDefault from './button';
import BloctoWalletIcon from '../../static/new_images/icon-wallet.svg';
import BloctoSwapIcon from '../../static/new_images/icon-swap.svg';
import BlcotoBayIcon from '../../static/new_images/icon-bay.svg';
import BloctoSdkIcon from '../../static/new_images/icon-sdk.svg';
import LngIcon from './lngIcon';

const PAGES = [
  {
    titleTranslationKey: 'blocto.header.product.wallet',
    descTranslationKey: 'blocto.header.product.wallet.desc',
    icon: BloctoWalletIcon,
    href: `/wallet`,
    isExternal: false,
    gtag: {
      event: 'bloctoweb_click_walletpage',
      properties: {
        location: 'header',
      },
    },
  },
  {
    titleTranslationKey: 'blocto.header.product.sdk',
    descTranslationKey: 'blocto.header.product.sdk.desc',
    icon: BloctoSdkIcon,
    href: `/sdk`,
    isExternal: false,
    gtag: {
      event: 'bloctoweb_click_sdkpage',
      properties: {
        location: 'header',
      },
    },
  },
  {
    titleTranslationKey: 'blocto.header.product.swap',
    descTranslationKey: 'blocto.header.product.swap.desc',
    icon: BloctoSwapIcon,
    href: `https://swap.blocto.app/`,
    isExternal: true,
    gtag: {
      event: 'bloctoweb_click_bloctoswap',
      properties: {
        location: 'header',
        link_url: `https://swap.blocto.app/`,
        link_domain: new URL(`https://swap.blocto.app/`).host,
      },
    },
  },
  {
    titleTranslationKey: 'blocto.header.product.bay',
    descTranslationKey: 'blocto.header.product.bay.desc',
    icon: BlcotoBayIcon,
    href: `https://bay.blocto.app/`,
    isExternal: true,
    gtag: {
      event: 'bloctoweb_click_bloctobay',
      properties: {
        location: 'header',
        link_url: `https://bay.blocto.app/`,
        link_domain: new URL(`https://bay.blocto.app/`).host,
      },
    },
  },
];

const Header = ({ showSmartBanner }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSmallerThan1000] = useMediaQuery('(max-width: 1440px)');

  const toggle = () => setIsExpanded((prev) => !prev);

  return (
    <Flex
      as="nav"
      alignItems="center"
      justifyContent="space-between"
      width={{ base: 'calc(100% - 48px)', md: 'calc(100% - 128px)', lg: '85%' }}
      maxWidth="1200px"
      px={{ lg: '16px' }}
      margin="0 auto"
      borderRadius="100px"
      position="fixed"
      top={{ base: showSmartBanner ? '82px' : '24px', lg: '40px' }}
      left="0"
      right="0"
      zIndex={100}
      _after={{
        display: 'block',
        content: "''",
        width: '100%',
        height: {
          base: '62px',
          md: '64px',
          lg: '70px',
        },
        bg: { lg: 'rgba(255, 255, 255, 0.70)' },
        backdropFilter: { lg: 'blur(20px)' },
        boxShadow: { lg: '0px 1px 15px 0px rgba(0, 0, 0, 0.05)' },
        position: 'absolute',
        left: 0,
        zIndex: -10,
        borderRadius: '100px',
      }}
    >
      <Flex
        alignItems="center"
        justifyContent={{ base: 'space-between', lg: 'inherit' }}
        width={{ base: '100%', lg: 'auto' }}
        height={{
          base: '62px',
          md: '64px',
          lg: '72px',
        }}
        px={{ base: '16px', lg: '0' }}
        borderRadius="100px"
        bg={{ base: 'rgba(255, 255, 255, 0.70)', lg: 'transparent' }}
        backdropFilter={{ base: 'blur(20px)', lg: 'none' }}
        boxShadow={{ base: '0px 1px 15px 0px rgba(0, 0, 0, 0.05)', lg: 'none' }}
      >
        <CustomLink
          to="/"
          title="blocto"
          flex="0 0 auto"
          ml="16px"
          gtag={{ event: 'bloctoweb_click_blocto', properties: { location: 'header' } }}
        >
          <Logo color="blue" />
        </CustomLink>

        <Button
          onClick={toggle}
          variant="icon"
          size="sm"
          display={{ base: 'block', lg: 'none' }}
          width="36px"
          minWidth="0"
          height="36px"
          px="0"
          py="0"
          borderRadius="50%"
        >
          {isExpanded ? <MenuCloseIcon /> : <MenuIcon fill="#00325A" />}
        </Button>
      </Flex>

      <Flex
        flexDirection={{ base: 'column', lg: 'row' }}
        alignItems="center"
        justifyContent={{ base: 'space-between', lg: 'unset' }}
        width={{ base: '100vw', lg: 'auto' }}
        height={{ base: showSmartBanner ? 'calc(100dvh - 58px)' : '100dvh', lg: 'unset' }}
        px={{ base: 'space.m', lg: '0' }}
        pt={{ base: '86px', lg: '0' }}
        pb={{ base: 'space.m', lg: '0' }}
        position={{ base: 'absolute', lg: 'unset' }}
        top="-24px"
        left={{
          base: isExpanded ? '50%' : 'calc(150% + 48px)',
          md: isExpanded ? '50%' : 'calc(150% + 128px)',
        }}
        transform={{ base: 'translateX(-50%)', lg: 'unset' }}
        zIndex={-1}
        transition="0.4s left ease-in-out"
        background={{ base: 'rgba(255, 255, 255, 0.80)', lg: 'transparent' }}
        boxShadow={{ base: '0px 1px 0px 0px rgba(0, 0, 0, 0.08)', lg: 'none' }}
        backdropFilter={{ base: 'blur(40px)', lg: 'none' }}
      >
        <Flex
          flexDirection={{ base: 'column', lg: 'row' }}
          width={{ base: '100%', lg: 'auto' }}
          position="relative"
        >
          <DropDownMenu
            items={PAGES}
            onClickCallback={toggle}
            mt={{ base: 'space.xl', lg: '0' }}
            mr={{ lg: 'space.4xs' }}
            mb={{ base: 'space.4xs', lg: '0' }}
          >
            <I18n id="blocto.header.product" />
          </DropDownMenu>
          <NavLink
            to="/web3-ecosystem"
            onClick={isSmallerThan1000 && isExpanded ? toggle : null}
            gtag={{ event: 'bloctoweb_click_ecosystem', properties: { location: 'header' } }}
            isExternal={false}
            mx={{ lg: 'space.4xs' }}
            my={{ base: 'space.4xs', lg: '0' }}
          >
            <I18n id="blocto.header.ecosystem" />
          </NavLink>
          <NavLink
            to="https://docs.blocto.app/"
            gtag={{
              event: 'bloctoweb_click_sdkdoc',
              properties: {
                location: 'header',
                link_url: 'https://docs.blocto.app/',
                link_domain: new URL('https://docs.blocto.app/').host,
              },
            }}
            onClick={isSmallerThan1000 && isExpanded ? toggle : null}
            mx={{ lg: 'space.4xs' }}
            my={{ base: 'space.4xs', lg: '0' }}
          >
            <I18n id="blocto.header.developer" />
          </NavLink>
          <NavLink
            to="/crypto-blog"
            gtag={{ event: 'bloctoweb_click_blog', properties: { location: 'header' } }}
            onClick={isSmallerThan1000 && isExpanded ? toggle : null}
            mx={{ lg: 'space.4xs' }}
            my={{ base: 'space.4xs', lg: '0' }}
          >
            <I18n id="blocto.header.blog" />
          </NavLink>
          <DropDownMenu
            items={[
              {
                titleTranslationKey: 'blocto.header.lng.en',
                onClick: () => {
                  window.location.href = '/';
                },
                gtag: {
                  event: 'bloctoweb_click_lng_en',
                  properties: {
                    location: 'header',
                  },
                },
              },
              {
                titleTranslationKey: 'blocto.header.lng.ja',
                onClick: () => {
                  changeLocale('ja', '/');
                },
                gtag: {
                  event: 'bloctoweb_click_lng_ja',
                  properties: {
                    location: 'header',
                  },
                },
              },
            ]}
            menuLeft="-20px"
            onClickCallback={toggle}
            mx={{ lg: 'space.4xs' }}
            my={{ base: 'space.4xs', lg: '0' }}
            noArrow
          >
            <LngIcon />
          </DropDownMenu>
        </Flex>

        <ButtonDefault
          size="sm"
          onClick={() => download('header')}
          width={{ base: '100%', lg: 'auto' }}
          fontSize="size.body.2"
          ml={{ base: '0', lg: 'space.l' }}
        >
          <I18n id="blocto.common.download" />
        </ButtonDefault>
      </Flex>
    </Flex>
  );
};

Header.propTypes = {
  showSmartBanner: PropTypes.bool,
};
Header.defaultProps = {
  showSmartBanner: false,
};

export default Header;

const NavLink = ({ to, isExternal = true, children, ...rest }) => (
  <CustomLink
    to={to}
    isExternal={isExternal}
    noHoverStyle
    px="space.m"
    py="space.s"
    borderRadius="100px"
    fontSize="16px"
    _hover={{ bg: 'interaction.secondary.hovered', color: 'font.highlight' }}
    {...rest}
  >
    {children}
  </CustomLink>
);

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isExternal: PropTypes.bool,
};

NavLink.defaultProps = {
  isExternal: true,
};
